import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Box } from 'lucide-react';

import { cn } from '@/lib/utils';
import { PreviewKind } from '../../../../common/hooks/use-assistant-api.hook';
import { readableOn, resolveLauncherColors } from '../../settings/models/chat-settings';
import { toChatSettings } from './blocks';

/**
 * What shoppers will see, drawn inside the conversation.
 *
 * Compact cousins of the settings pages' previews (chat-preview.tsx), sized for
 * a 380px panel. The colours go through the same resolver the settings page
 * and the SDK use, so "automatic" here means what it means on their site.
 */

/** A product page mock, the stage both previews are drawn on. */
const Stage: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <div className={cn('relative overflow-hidden rounded-xl border bg-white p-3 text-[#1a1a2e]', className)}>
        <div className="flex gap-3">
            <div className="aspect-square w-[38%] shrink-0 rounded-lg bg-gradient-to-br from-stone-200 to-stone-300" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="h-3.5 w-3/4 rounded bg-stone-200" />
                <div className="h-2 w-1/3 rounded bg-stone-100" />
                <div className="h-2 w-full rounded bg-stone-100" />
                <div className="h-2 w-5/6 rounded bg-stone-100" />
                <div className="mt-1 h-6 w-full rounded-md bg-[#1a1a2e]" />
                {children}
            </div>
        </div>
    </div>
);

export const ShoppingAssistantPreview: React.FC<{ settings: Record<string, any> }> = ({ settings }) => {
    const { t } = useTranslation();
    const [hover, setHover] = useState(false);
    const chat = toChatSettings(settings);
    const colors = resolveLauncherColors(chat, settings.primaryColor, settings.secondaryColor);
    const left = chat.position === 'left';
    const questions = [
        t('CHAT.PREVIEW.QUESTION_1', 'Is this suitable for a bathroom floor?'),
        t('CHAT.PREVIEW.QUESTION_2', 'What grout colour works best with it?'),
    ];
    const blockText = readableOn(colors.background);
    const off = settings.enabled === false;

    return (
        <div className={cn('flex flex-col gap-2', off && 'opacity-60')}>
            <Stage>
                {chat.prompts?.enabled ? (
                    <div className="mt-1.5 flex flex-col gap-1">
                        <span className="text-[11px] font-semibold">
                            {chat.prompts.heading || t('CHAT.PROMPTS.HEADING_PLACEHOLDER', 'A question about this product?')}
                        </span>
                        <div className="flex flex-wrap gap-1">
                            {[...questions, chat.prompts.ctaLabel || t('CHAT.PROMPTS.CTA_PLACEHOLDER', 'Ask my question')].map((q, i) => (
                                <span
                                    key={q}
                                    className={cn('rounded-lg px-2 py-1 text-[10px] leading-tight', i === questions.length && 'font-semibold')}
                                    style={{ backgroundColor: colors.background, color: blockText }}
                                >
                                    {q}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="mt-1.5 text-[10px] text-stone-400">{t('CHAT.PREVIEW.PROMPTS_OFF', 'The questions block is off.')}</p>
                )}
            </Stage>

            <div className={cn('flex flex-col gap-1.5', left ? 'items-start' : 'items-end')}>
                {chat.greeting && (
                    <span className="max-w-[85%] rounded-2xl border bg-white px-3 py-1.5 text-xs text-[#1a1a2e] shadow-sm">
                        {chat.greeting}
                    </span>
                )}
                <button
                    type="button"
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    className="inline-flex h-10 items-center gap-2 rounded-full pl-3 pr-4 text-xs font-semibold shadow-md transition-colors"
                    style={{
                        backgroundColor: hover ? colors.hoverBackground : colors.background,
                        color: hover ? colors.hoverText : colors.text,
                    }}
                >
                    <MessageCircle className="size-5" strokeWidth={1.7} />
                    {chat.label || t('CHAT.APPEARANCE.LABEL_PLACEHOLDER', 'Shopping Assistant')}
                </button>
            </div>
            {off && <p className="text-xs text-muted-foreground">{t('AGENTS.UI.PREVIEW.CHAT_OFF')}</p>}
        </div>
    );
};

export const VisualizerButtonPreview: React.FC<{ settings: Record<string, any> }> = ({ settings: s }) => {
    const { t } = useTranslation();
    const hasSiteButton = s.buttonText !== undefined || s.buttonColor !== undefined;
    const hasPanel = s.primaryColor !== undefined || s.orderButtonLabel !== undefined || s.cornerRadius !== undefined;
    const bg = s.buttonColor || s.primaryColor || '#3B82F6';
    const style = s.buttonStyle || 'filled';
    const radius = Number.isFinite(Number(s.buttonCorners)) ? Number(s.buttonCorners) : 8;
    const primary = s.primaryColor || '#1a1a2e';
    const panelRadius = Number.isFinite(Number(s.cornerRadius)) ? Number(s.cornerRadius) : 8;

    return (
        <div className="flex flex-col gap-2">
            {hasSiteButton && (
                <Stage>
                    <span
                        className={cn(
                            'mt-1.5 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium',
                            style === 'link' && 'px-0 underline',
                        )}
                        style={{
                            borderRadius: radius,
                            backgroundColor: style === 'filled' ? bg : 'transparent',
                            color: style === 'filled' ? s.buttonTextColor || readableOn(bg) : bg,
                            border: style === 'outlined' ? `1.5px solid ${bg}` : undefined,
                        }}
                    >
                        <Box className="size-3.5" />
                        {s.buttonText || t('AGENTS.UI.PREVIEW.BUTTON_DEFAULT')}
                    </span>
                </Stage>
            )}

            {hasPanel && (
                <div
                    className="overflow-hidden rounded-xl border bg-white text-[#1a1a2e]"
                    style={{ fontFamily: s.fontFamily || undefined }}
                >
                    <div className="flex items-center gap-2 px-3 py-2 text-xs font-medium" style={{ backgroundColor: primary, color: readableOn(primary) }}>
                        {s.logo ? <img src={s.logo} alt="" className="h-4 max-w-[60px] object-contain" /> : null}
                        {t('AGENTS.UI.PREVIEW.VISUALIZER')}
                    </div>
                    <div className="flex gap-2 p-3">
                        <div className="aspect-[4/3] flex-1 rounded-md bg-gradient-to-br from-stone-200 to-stone-300" />
                        <div className="flex w-[40%] flex-col gap-1.5">
                            <div className="h-2 w-full rounded bg-stone-100" />
                            <div className="h-2 w-2/3 rounded bg-stone-100" />
                            <span
                                className="mt-auto inline-flex justify-center px-2 py-1.5 text-[11px] font-medium"
                                style={{ backgroundColor: primary, color: readableOn(primary), borderRadius: panelRadius }}
                            >
                                {s.orderButtonLabel || t('AGENTS.UI.PREVIEW.ORDER_DEFAULT')}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/** The registry: a preview kind to the component that draws it. */
export const PREVIEWS: Record<PreviewKind, React.FC<{ settings: Record<string, any> }>> = {
    shopping_assistant: ShoppingAssistantPreview,
    visualizer_button: VisualizerButtonPreview,
};

export const PreviewFor: React.FC<{ kind: PreviewKind; settings: Record<string, any>; title?: string }> = ({ kind, settings, title }) => {
    const Component = PREVIEWS[kind];
    if (!Component) return null;
    return (
        <div className="mt-2.5 rounded-xl border bg-muted/40 p-2.5">
            {title && <p className="mb-2 text-xs font-medium text-muted-foreground">{title}</p>}
            {/* A shopper's view is narrow; on the full-width page it would stretch into a banner. */}
            <div className="max-w-[420px]">
                <Component settings={settings} />
            </div>
        </div>
    );
};
